import { DocumentV1Payload } from '@taxfyle/documents-api-client'
import {
  DocumentLinkResource,
  DocumentRejectionResource,
  DocumentResource,
  DocumentStageResource,
  ManualAttestationReasonResource,
  PreprocessingPhaseResource,
  SearchDocumentsRequest,
} from '@taxfyle/documents-api-client/v5'
import { PrimeMemberByPublicId } from 'iam/MemberStore'
import { PrimeWorkspace } from 'iam/WorkspaceStore'
import { Direction, Id } from 'types/definitions'
import { maybeParseDate } from 'utils/date-util'
import { match } from 'utils/pattern-match-util'
import Document from './Document'
import { DocumentLinks } from './DocumentLinks'
import { PrimeDocumentTypeByShortId } from './DocumentTypeStore'
import { DocumentArchival, DocumentRetention } from './DocumentRetention'
import {
  DocumentRejection,
  DocumentStage,
  ManualAttestationReason,
  PreprocessingPhase,
} from './DocumentStage'
import type {
  AdminDocumentsQuery,
  JobDocumentsQuery,
  SearchDocumentsQuery,
  UserDocumentsQuery,
} from './DocumentStore'

/**
 * Maps a document resource to document props, priming the related
 * models along the way.
 */
export function mapDocumentResourceToProps(
  resource: DocumentResource,
  primeMemberByPublicId: PrimeMemberByPublicId,
  primeWorkspace: PrimeWorkspace,
  primeDocumentTypeByShortId: PrimeDocumentTypeByShortId,
): Partial<Document> {
  return {
    id: Id(resource.id),
    title: resource.title,
    workspaceId: Id(resource.workspace_id),
    archived: resource.archived,
    // The v5 API has no edit permission concept so true by default.
    canEdit: true,
    dateCreated: maybeParseDate(resource.date_created),
    dateModified: maybeParseDate(resource.date_modified),
    stage: mapResourceToDocumentStage(resource),
    retention: mapResourceToDocumentRetention(resource),
    links: mapResourceToDocumentLinks(resource.links),
    owner: primeMemberByPublicId({
      user_public_id: resource.owner_id,
      workspace_id: resource.workspace_id,
    }),
    author: primeMemberByPublicId({
      user_public_id: resource.author_id,
      workspace_id: resource.workspace_id,
    }),
    workspace: primeWorkspace({ id: resource.workspace_id }),
    // The v5 API identifies document types by their short id.
    documentType: resource.document_type
      ? primeDocumentTypeByShortId(
          resource.document_type.id,
          resource.document_type.name,
        )
      : null,
  }
}

/**
 * Maps a search documents query to the search documents request.
 */
export function mapSearchDocumentsQueryToSearchRequest(
  query: SearchDocumentsQuery,
): SearchDocumentsRequest {
  const base = {
    order_by: query.orderBy?.field ?? 'date_created',
    order: query.orderBy?.direction ?? Direction.Descending,
    limit: query.limit,
    after: query.after ?? undefined,
    archived: query.archived,
  }

  return match<SearchDocumentsQuery['context'], SearchDocumentsRequest>(
    query.context,
    {
      Job: () => {
        const jobQuery = query as JobDocumentsQuery
        return {
          ...base,
          context: 'JOB',
          job_id: Id.assert(jobQuery.jobId),
          ...cleanFilters({ workspace_id: jobQuery.workspaceId }),
        }
      },
      User: () => {
        const userQuery = query as UserDocumentsQuery
        return {
          ...base,
          context: 'USER',
          ...cleanFilters({ workspace_id: userQuery.workspaceId }),
        }
      },
      Admin: () => {
        const adminQuery = query as AdminDocumentsQuery
        return {
          ...base,
          context: 'ADMIN',
          ...cleanFilters({
            search_text: adminQuery.searchText || undefined,
            owner_public_ids: adminQuery.ownerPublicIds,
            workspace_ids: adminQuery.workspaceIds,
            job_ids: adminQuery.jobIds,
            document_type_ids: adminQuery.documentTypeIds,
          }),
        }
      },
    },
  )
}

/**
 * Removes undefined values and empty arrays from the filters, so they
 * don't end up in the request's query parameters.
 */
function cleanFilters<T extends object>(filters: T): Partial<T> {
  return Object.fromEntries(
    Object.entries(filters).filter(
      ([, value]) =>
        value !== undefined && (!Array.isArray(value) || value.length > 0),
    ),
  ) as Partial<T>
}

/**
 * Maps a v1 realtime document payload to document props, priming the
 * related models along the way.
 */
export function mapDocumentV1PayloadToProps(
  payload: DocumentV1Payload,
  primeMemberByPublicId: PrimeMemberByPublicId,
  primeWorkspace: PrimeWorkspace,
  primeDocumentTypeByShortId: PrimeDocumentTypeByShortId,
): Partial<Document> {
  return {
    id: Id(payload.id),
    title: payload.title,
    workspaceId: Id(payload.workspace_id),
    archived: payload.archived,
    canEdit: true,
    dateCreated: maybeParseDate(payload.date_created),
    dateModified: maybeParseDate(payload.date_modified),
    stage: mapResourceToDocumentStage(payload),
    retention: mapResourceToDocumentRetention(payload),
    links: {
      job: { jobIds: payload.links.job.job_ids.map((id) => Id(id)) },
      workRequest: {
        workRequestId: payload.links.work_request.work_request_id
          ? Id(payload.links.work_request.work_request_id)
          : null,
      },
      strategy: {
        folderId: payload.links.strategy.folder_id
          ? Id(payload.links.strategy.folder_id)
          : null,
      },
    },
    owner: primeMemberByPublicId({
      user_public_id: payload.owner_public_id,
      workspace_id: payload.workspace_id,
    }),
    author: primeMemberByPublicId({
      user_public_id: payload.author_id,
      workspace_id: payload.workspace_id,
    }),
    workspace: primeWorkspace({ id: payload.workspace_id }),
    // The payload always states its type, so a missing one genuinely
    // means untyped and clears it. The v5 API identifies document
    // types by their short id.
    documentType: payload.document_type
      ? primeDocumentTypeByShortId(
          payload.document_type.id,
          payload.document_type.name,
        )
      : null,
    ...(payload.description !== undefined && {
      description: payload.description,
    }),
  }
}

/**
 * The stage fields shared by the document resource and the v1
 * realtime payload.
 */
interface StageFields {
  stage: DocumentStageResource
  preprocessing_phase: PreprocessingPhaseResource | null
  rejection: DocumentRejectionResource | null
  manual_attestation_reason: ManualAttestationReasonResource | null
}

/**
 * The retention fields shared by the document resource and the v1
 * realtime payload.
 */
interface RetentionFields {
  archived: boolean
  date_archived: string | null
  archived_by_user_public_id: string | null
  purged: boolean
}

/**
 * Maps the stage fields of a document resource to the document stage.
 */
export function mapResourceToDocumentStage(
  resource: StageFields,
): DocumentStage {
  return match<DocumentStageResource, DocumentStage>(resource.stage, {
    PREPROCESSING: () => ({
      type: 'Preprocessing',
      phase: mapPreprocessingPhase(resource.preprocessing_phase),
    }),
    ATTESTATION_SCREENING: () => ({
      type: 'AttestationScreening',
    }),
    MANUAL_ATTESTATION_REQUIRED: () => ({
      type: 'ManualAttestationRequired',
      reason: mapManualAttestationReason(resource.manual_attestation_reason),
    }),
    RELEASED: () => ({ type: 'Released' }),
    REJECTED: () => ({
      type: 'Rejected',
      rejection: mapDocumentRejection(resource.rejection),
    }),
  })
}

/**
 * Maps a document links resource to the document links.
 */
export function mapResourceToDocumentLinks(
  links: DocumentLinkResource,
): DocumentLinks {
  return {
    job: { jobIds: links.job.job_ids.map((id) => Id(id)) },
    workRequest: {
      workRequestId: links.work_request
        ? Id(links.work_request.work_request_id)
        : null,
    },
    strategy: {
      folderId: links.strategy ? Id(links.strategy.folder_id) : null,
    },
  }
}

/**
 * Maps the retention fields of a document resource to the
 * document retention.
 */
export function mapResourceToDocumentRetention(
  resource: RetentionFields,
): DocumentRetention {
  const archival = mapDocumentArchival(resource)

  if (resource.purged) {
    // Purge details are not exposed by the API.
    return { type: 'Purged', archival, purge: null }
  }

  if (resource.archived) {
    return { type: 'Archived', archival }
  }

  return DocumentRetention.active
}

/**
 * Maps the archival fields of a document resource to the
 * archival information.
 */
function mapDocumentArchival(
  resource: RetentionFields,
): DocumentArchival | null {
  if (!resource.date_archived || !resource.archived_by_user_public_id) {
    return null
  }

  return {
    archivedAt: new Date(resource.date_archived),
    archivedBy: Id(resource.archived_by_user_public_id),
  }
}

/**
 * Maps a preprocessing phase resource to the document preprocessing phase.
 */
function mapPreprocessingPhase(
  phase: PreprocessingPhaseResource | null,
): PreprocessingPhase {
  if (!phase) {
    return 'VirusScanning'
  }

  return match<PreprocessingPhaseResource, PreprocessingPhase>(phase, {
    CHECKING_FOR_FILE_CONVERSION: () => 'CheckingForFileConversion',
    CONVERTING_FILE: () => 'ConvertingFile',
    FINALIZING: () => 'Finalizing',
    VIRUS_SCANNING: () => 'VirusScanning',
  })
}

/**
 * Maps a manual attestation reason resource to the document reason.
 */
export function mapManualAttestationReason(
  reason: ManualAttestationReasonResource | null,
): ManualAttestationReason | undefined {
  if (!reason) {
    return undefined
  }

  return match<ManualAttestationReasonResource, ManualAttestationReason>(
    reason,
    {
      CLIENT_NAME_MISMATCH: () => 'ClientNameMismatch',
      MISSING_PRO_SIGNATURE: () => 'MissingProSignature',
      EXTRACTION_FAULTED: () => 'ExtractionFaulted',
      ATTESTATION_FAULTED: () => 'AttestationFaulted',
      TIMED_OUT: () => 'TimedOut',
    },
  )
}

/**
 * Maps a document rejection resource to the document rejection.
 */
function mapDocumentRejection(
  rejection: DocumentRejectionResource | null,
): DocumentRejection {
  return rejection === 'QUARANTINED' ? 'Quarantined' : 'ConversionFailed'
}
